import { useState, useEffect } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon, CheckIcon } from 'lucide-react';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { Button } from '@/components/ui/button';
import { FormSchemaDialog } from '@/components/shared/form-schema-dialog';
import { formsApi } from '@/lib/api/forms';
import { ApiRequestError } from '@/lib/api/client';
import { FormSubmissionsPanel } from '@/components/shared/form-submissions-panel';
import type { FormCreateInput, FormUpdateInput, Form, ResourceOperation, Tenant } from '@/types/entities';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

type DestructiveUpdateError = {
  requiresForce?: boolean;
  warnings?: string[];
};

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

type FormsManagementPanelProps = {
  tenant: Tenant;
  initialFormName?: string;
  onFormSelect?: (formName: string | null) => void;
  submissionsEditorMode?: 'create' | 'edit' | null;
  editingSubmissionId?: number | null;
  onSubmissionsEditorModeChange?: (mode: 'create' | 'edit' | null, submissionId?: number | null) => void;
}

export function FormsManagementPanel({
  tenant,
  initialFormName,
  onFormSelect,
  submissionsEditorMode = null,
  editingSubmissionId = null,
  onSubmissionsEditorModeChange,
}: FormsManagementPanelProps) {
  const [forms, setForms] = useState<Form[]>([]);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingForm, setEditingForm] = useState<Form | undefined>();
  const [formToDelete, setFormToDelete] = useState<Form | null>(null);
  const [pendingForceUpdate, setPendingForceUpdate] = useState<FormUpdateInput | null>(null);
  const [destructiveWarnings, setDestructiveWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copiedFormId, setCopiedFormId] = useState<number | null>(null);

  const getAllowedOps = (form: Form): ResourceOperation[] => {
    const configuredOps = form.details.ops?.length ? form.details.ops : ALL_OPERATIONS;
    const uniqueOps = Array.from(new Set<ResourceOperation>(configuredOps));
    return ALL_OPERATIONS.filter((operation) => uniqueOps.includes(operation));
  };

  const getSubmissionUrl = (form: Form) => {
    return `/api/v1/public/tenants/${tenant.slug}/forms/${form.endpoint}/submit`;
  };

  const copyToClipboard = async (form: Form) => {
    try {
      await navigator.clipboard.writeText(getSubmissionUrl(form));
      setCopiedFormId(form.id);
      setTimeout(() => setCopiedFormId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const fetchForms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await formsApi.getAllForTenant(tenant.slug);
      setForms(data);

      // If there's an initialFormName, try to select it
      if (initialFormName && !selectedForm) {
        const targetForm = data.find(f => f.name === initialFormName);
        if (targetForm) {
          const fullForm = await formsApi.getById(tenant.slug, targetForm.id);
          setSelectedForm(fullForm);
          onFormSelect?.(targetForm.name);
        }
      } else if (data.length > 0 && !selectedForm && !initialFormName) {
        // Auto-select the first form if no initialFormName is provided
        const fullForm = await formsApi.getById(tenant.slug, data[0].id);
        setSelectedForm(fullForm);
        onFormSelect?.(data[0].name);
      }
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to load forms');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchForms();
  }, [tenant.slug]);

  // Handle initialFormName changes (e.g., when query param changes)
  useEffect(() => {
    if (initialFormName && forms.length > 0) {
      const targetForm = forms.find(f => f.name === initialFormName);
      if (targetForm && selectedForm?.name !== initialFormName) {
        handleSelect(targetForm);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFormName, forms]);

  const handleCreate = () => {
    setEditingForm(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = async (form: Form) => {
    try {
      const fullForm = await formsApi.getById(tenant.slug, form.id);
      setEditingForm(fullForm);
      setIsDialogOpen(true);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to load form');
    }
  };

  const handleSelect = async (form: Form) => {
    try {
      const fullForm = await formsApi.getById(tenant.slug, form.id);
      setSelectedForm(fullForm);
      onFormSelect?.(form.name);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to load form');
    }
  };

  const handleDelete = (form: Form) => {
    setFormToDelete(form);
  };

  const confirmDelete = async () => {
    if (!formToDelete) return;

    try {
      await formsApi.delete(tenant.slug, formToDelete.id);
      if (selectedForm?.id === formToDelete.id) {
        setSelectedForm(null);
      }
      setFormToDelete(null);
      fetchForms();
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to delete form');
    }
  };

  const handleSubmit = async (data: FormCreateInput | FormUpdateInput) => {
    if (editingForm) {
      try {
        await formsApi.update(tenant.slug, editingForm.id, data as FormUpdateInput);
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 409) {
          const errorData = err.data as DestructiveUpdateError | undefined;
          if (errorData?.requiresForce) {
            setPendingForceUpdate(data as FormUpdateInput);
            setDestructiveWarnings(errorData.warnings ?? []);
            return;
          }
        }
        throw err;
      }
    } else {
      await formsApi.create(tenant.slug, data as FormCreateInput);
    }
    fetchForms();
    setIsDialogOpen(false);
    setEditingForm(undefined);
  };

  const confirmForceUpdate = async () => {
    if (!editingForm || !pendingForceUpdate) {
      return;
    }

    try {
      await formsApi.update(tenant.slug, editingForm.id, {
        ...pendingForceUpdate,
        force: true,
      });
      await fetchForms();
      setPendingForceUpdate(null);
      setDestructiveWarnings([]);
      setIsDialogOpen(false);
      setEditingForm(undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply destructive update');
    }
  };

  const getFieldCount = (form: Form) => {
    return form.details.columns.length;
  };

  return (
    <>
      <ResizablePanelGroup direction="horizontal" className="h-[calc(100vh-13rem)] min-h-[600px] border rounded-lg overflow-hidden">
      {/* Left Panel: Forms List */}
      <ResizablePanel defaultSize={40} minSize={30} className="bg-muted/20">
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b bg-background">
            <div>
              <h3 className="font-semibold">Forms</h3>
              <p className="text-xs text-muted-foreground">{forms.length} forms</p>
            </div>
            <Button size="sm" onClick={handleCreate} className="gap-1">
              <PlusIcon className="size-4" />
              New
            </Button>
          </div>

          {error && (
            <div className="p-4 text-destructive text-sm">{error}</div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center flex-1">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : forms.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-center p-8">
              <div className="text-muted-foreground mb-2">No forms yet</div>
              <p className="text-sm text-muted-foreground mb-4">Create your first form to get started</p>
              <Button size="sm" onClick={handleCreate} className="gap-1">
                <PlusIcon className="size-4" />
                Create Form
              </Button>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {forms.map((form) => (
                <div
                  key={form.id}
                  className={cn(
                    "p-4 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-muted/50",
                    selectedForm?.id === form.id && "bg-muted"
                  )}
                  onClick={() => handleSelect(form)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{form.display_name}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary" className="font-mono text-xs">
                          /{form.endpoint}
                        </Badge>
                        <Badge variant="outline" className="font-mono text-xs">
                          {form.name}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {getFieldCount(form)} fields
                      </p>
                      <div className="mb-2 flex flex-wrap gap-1">
                        {getAllowedOps(form).map((operation) => (
                          <Badge key={`${form.id}-${operation}`} variant="outline" className="text-[10px] capitalize">
                            {operation}
                          </Badge>
                        ))}
                      </div>
                      <div
                        className="flex items-center gap-2 text-xs text-muted-foreground group"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>POST</span>
                        <code className="bg-muted px-2 py-0.5 rounded font-mono flex-1 truncate">
                          {getSubmissionUrl(form)}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(form)}
                          className="h-6 w-6 p-0 gap-1 opacity-60 group-hover:opacity-100 transition-opacity"
                          title="Copy endpoint URL"
                        >
                          {copiedFormId === form.id ? (
                            <CheckIcon className="size-3 text-green-600" />
                          ) : (
                            <CopyIcon className="size-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <div
                      className="flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleEdit(form)}
                        title="Edit schema"
                      >
                        <EditIcon className="size-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(form)}
                        title="Delete"
                      >
                        <TrashIcon className="size-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle />

      {/* Right Panel: Form Submissions */}
      <ResizablePanel defaultSize={60} minSize={40} className="bg-background overflow-auto">
        {selectedForm ? (
          <div className="h-full overflow-y-auto p-4">
            <FormSubmissionsPanel
              tenant={{
                id: tenant.id,
                slug: tenant.slug,
                name: tenant.name,
                websiteUrl: tenant.website_url,
              }}
              form={selectedForm}
              onFormChange={fetchForms}
              editorMode={submissionsEditorMode}
              editingSubmissionId={editingSubmissionId}
              onCreateRoute={() => onSubmissionsEditorModeChange?.('create')}
              onEditRoute={(submissionId) => onSubmissionsEditorModeChange?.('edit', submissionId)}
              onExitEditorRoute={() => onSubmissionsEditorModeChange?.(null)}
            />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <div className="text-muted-foreground mb-2">Select a form</div>
            <p className="text-sm text-muted-foreground">
              Choose a form from the left panel to view and manage its submissions
            </p>
          </div>
        )}
      </ResizablePanel>

      <FormSchemaDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsDialogOpen(false);
          setEditingForm(undefined);
          setPendingForceUpdate(null);
          setDestructiveWarnings([]);
        }}
        form={editingForm}
        title="Form"
        description={editingForm ? 'Update the schema for this form.' : 'Define the schema for this form.'}
      />
      </ResizablePanelGroup>

      <ConfirmationDialog
        open={!!formToDelete}
        onOpenChange={(open) => !open && setFormToDelete(null)}
        title="Delete Form"
        description={`Are you sure you want to delete "${formToDelete?.display_name}"? This will also delete all submissions for this form.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />

      <ConfirmationDialog
        open={!!pendingForceUpdate}
        onOpenChange={(open) => {
          if (!open) {
            setPendingForceUpdate(null);
            setDestructiveWarnings([]);
          }
        }}
        title="Apply destructive schema update?"
        description={
          <div className="space-y-2 text-left">
            <p>This update may break data integrity for existing submissions.</p>
            {destructiveWarnings.length > 0 ? (
              <ul className="list-disc pl-4 space-y-1">
                {destructiveWarnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            ) : null}
          </div>
        }
        onConfirm={confirmForceUpdate}
        confirmVariant="destructive"
        confirmLabel="Force Update"
      />
    </>
  );
}
